import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, Calculator, Trash2 } from "lucide-react";

export default function GaugeRRPage() {
  const { projectId } = useParams();
  const [studies, setStudies] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ name: "", partName: "", characteristic: "", numberOfOperators: 3, numberOfParts: 5, numberOfTrials: 2 });
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/gauge-rr`);
    setStudies(data);
    setSel((s) => (s ? data.find((x) => x.id === s.id) || data[0] || null : data[0] || null));
  }, [projectId]);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const create = async () => {
    try { await api.post(`/projects/${projectId}/gauge-rr`, form); setShowNew(false); load(); toast.success("Study created"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const setCell = (key, v) => setSel({ ...sel, measurements: { ...(sel.measurements || {}), [key]: v } });

  const compute = async () => {
    try {
      await api.put(`/projects/${projectId}/gauge-rr/${sel.id}/measurements`, { measurements: sel.measurements || {} });
      const { data } = await api.post(`/projects/${projectId}/gauge-rr/${sel.id}/compute`);
      setSel({ ...sel, results: data });
      toast.success(`%GRR = ${data.gaugeRrPercent}%`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="gauge-rr-page">
      <PageHeader overline="Six Sigma Toolkit · Measurement System Analysis" title="Gauge R&R"
        actions={<Button data-testid="new-study-btn" className="rounded-none" onClick={() => setShowNew(!showNew)}><Plus className="h-4 w-4 mr-1" />New Study</Button>} />

      {showNew && (
        <div className="border border-border bg-card p-4 mb-4 grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
          <div className="col-span-2"><Label>Study name</Label><Input data-testid="study-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Part</Label><Input value={form.partName} onChange={(e) => setForm({ ...form, partName: e.target.value })} /></div>
          <div><Label>Operators (2-3)</Label><Input type="number" value={form.numberOfOperators} onChange={(e) => setForm({ ...form, numberOfOperators: +e.target.value })} /></div>
          <div><Label>Parts (2-10)</Label><Input type="number" value={form.numberOfParts} onChange={(e) => setForm({ ...form, numberOfParts: +e.target.value })} /></div>
          <div className="flex gap-2 items-end">
            <div><Label>Trials (2-3)</Label><Input type="number" value={form.numberOfTrials} onChange={(e) => setForm({ ...form, numberOfTrials: +e.target.value })} /></div>
            <Button data-testid="create-study-btn" className="rounded-none" onClick={create} disabled={!form.name}>Create</Button>
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-4">
        {studies.map((s) => (
          <button key={s.id} data-testid="study-tab" onClick={() => setSel(s)}
            className={`border px-3 py-1.5 text-sm ${sel?.id === s.id ? "border-[#1E6FE8] text-[#1E6FE8] bg-[#1E6FE8]/5 font-semibold" : "border-border"}`}>
            {s.name}
          </button>
        ))}
      </div>

      {sel && (
        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          <div className="border border-border bg-card overflow-auto">
            <div className="px-4 py-2.5 border-b border-border flex justify-between items-center">
              <span className="overline-label">Measurements grid — Xbar-R (AIAG)</span>
              <div className="flex gap-2">
                <Button data-testid="compute-grr-btn" size="sm" className="rounded-none h-7" onClick={compute}><Calculator className="h-3.5 w-3.5 mr-1" />Compute</Button>
                <button onClick={async () => { await api.delete(`/projects/${projectId}/gauge-rr/${sel.id}`); setSel(null); load(); }} className="p-1 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <table className="w-full data-table text-sm">
              <thead>
                <tr><th>Part</th>
                  {Array.from({ length: sel.numberOfOperators }).map((_, o) =>
                    Array.from({ length: sel.numberOfTrials }).map((_, t) => (
                      <th key={`${o}-${t}`}>Op {o + 1} · T{t + 1}</th>
                    )))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: sel.numberOfParts }).map((_, p) => (
                  <tr key={p}>
                    <td className="font-semibold">Part {p + 1}</td>
                    {Array.from({ length: sel.numberOfOperators }).map((_, o) =>
                      Array.from({ length: sel.numberOfTrials }).map((_, t) => (
                        <td key={`${o}-${p}-${t}`} className="!p-1">
                          <input data-testid="grr-cell" type="number" step="0.001"
                            value={(sel.measurements || {})[`${o}-${p}-${t}`] ?? ""}
                            onChange={(e) => setCell(`${o}-${p}-${t}`, e.target.value)}
                            className="w-20 border border-input px-1.5 py-1 text-xs bg-background" />
                        </td>
                      )))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border border-border bg-card p-4 h-fit" data-testid="grr-results">
            <div className="overline-label mb-2">Results</div>
            {sel.results ? (
              <div className="space-y-2 text-sm">
                {[["Repeatability (EV)", sel.results.repeatability], ["Reproducibility (AV)", sel.results.reproducibility],
                  ["Gauge R&R", sel.results.gaugeRr], ["Part-to-Part (PV)", sel.results.partToPart],
                  ["Total Variation", sel.results.totalVariation], ["Distinct Categories (ndc)", sel.results.distinctCategories]].map(([l, v]) => (
                  <div key={l} className="flex justify-between border-b border-border pb-1"><span className="text-muted-foreground">{l}</span><span className="font-mono2">{v}</span></div>
                ))}
                <div className="flex justify-between items-center pt-1">
                  <span className="font-semibold">%GRR</span>
                  <StatusBadge value={`${sel.results.gaugeRrPercent}%`} tone={sel.results.gaugeRrPercent < 10 ? "green" : sel.results.gaugeRrPercent < 30 ? "amber" : "red"} />
                </div>
                <p className="text-xs text-muted-foreground pt-1">{sel.results.conclusion}</p>
              </div>
            ) : <p className="text-xs text-muted-foreground">Fill the grid and hit Compute.</p>}
          </div>
        </div>
      )}
      {studies.length === 0 && !showNew && <div className="border border-dashed border-border p-10 text-center text-muted-foreground">No studies yet — create one to run measurement system analysis.</div>}
    </div>
  );
}
